import moment from "jalali-moment";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import company from "../../../config/company";
import endpoints from "../../../config/endpoints";
import restApi from "../../../services/restApi";
import tools from "../../../utils/tools";
import "./print.css";

/**
 * A4 invoice — order header, line items and the payable breakdown.
 * `order.price` is the pre-discount product total and `order.discountPrice`
 * is the total after product-level discounts (see OrderService.calculatePrice),
 * so the discount line is the difference between the two.
 */
const Invoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<any>({});

  const fetchData = async () => {
    const res = await restApi(endpoints.order.index + id, true).get();
    setData(res.data || {});
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fa = (value: any) => tools.engNumToPersian(value ?? "") || "";
  const price = (value: number) =>
    `${tools.engNumToPersian(tools.formatPrice(value || 0))} ${company.currency}`;

  const address = data?.delivery?.address;
  const total = Number(data?.price) || 0;
  const discounted = Number(data?.discountPrice) || total;
  // product-level discounts and the flat discount the operator applied by hand
  // are shown as one line; both come off before shipping is added
  const discount =
    Math.max(0, total - discounted) + (Number(data?.adminDiscount) || 0);
  const deliveryPrice = Number(data?.delivery?.price) || 0;
  const payable = Math.max(0, total - discount) + deliveryPrice;

  return (
    <div className="printPage">
      {/* A4 portrait — scoped to this route so the label keeps its own size */}
      <style>{"@page { size: A4 portrait; margin: 12mm; }"}</style>

      <div className="printToolbar">
        <button className="primary" onClick={() => window.print()}>
          چاپ فاکتور
        </button>
        <button onClick={() => navigate(-1)}>بازگشت</button>
      </div>

      <div className="invoiceSheet">
        <div className="invoiceHead">
          <div>
            شماره فاکتور :{" "}
            {company.invoicePrefix
              ? `${company.invoicePrefix}-${fa(data?.code)}`
              : fa(data?.code)}
          </div>
          <div>
            تاریخ :{" "}
            {data?.createdAt
              ? fa(moment(data.createdAt).locale("fa").format("jD / jMMMM / jYYYY"))
              : "—"}
          </div>
        </div>

        <div className="invoiceBuyer">
          <div>
            خریدار :{" "}
            {[data?.user?.name, data?.user?.lastName].filter(Boolean).join(" ") || "—"} -{" "}
            {fa(data?.user?.phoneNumber)}
          </div>
          <div>
            {[
              address?.province?.title,
              address?.city?.title,
              address?.description,
            ]
              .filter(Boolean)
              .join(" - ") || "—"}
          </div>
        </div>

        <table className="invoiceTable">
          <thead>
            <tr>
              <th style={{ width: "6%" }}>ردیف</th>
              <th style={{ width: "12%" }}>کد کالا</th>
              <th style={{ width: "38%" }}>شرح کالا</th>
              <th style={{ width: "8%" }}>تعداد</th>
              <th style={{ width: "18%" }}>قیمت واحد ({company.currency})</th>
              <th style={{ width: "18%" }}>قیمت کل ({company.currency})</th>
            </tr>
          </thead>
          <tbody>
            {(data?.products || []).map((orderProduct, index) => (
              <tr key={orderProduct.id}>
                <td>{fa(index + 1)}</td>
                <td>{orderProduct?.product?.code || "---"}</td>
                <td className="rowDesc">{orderProduct?.product?.title || "—"}</td>
                <td>{fa(orderProduct?.count)}</td>
                <td>{tools.engNumToPersian(tools.formatPrice(orderProduct?.price))}</td>
                <td>
                  {tools.engNumToPersian(
                    tools.formatPrice(
                      (Number(orderProduct?.price) || 0) *
                        (Number(orderProduct?.count) || 0),
                    ),
                  )}
                </td>
              </tr>
            ))}

            <tr>
              <td className="methodCell" colSpan={3} rowSpan={4}>
                <div>
                  روش ارسال : {data?.delivery?.deliveryMethod?.title || "—"}
                </div>
                <div>
                  روش پرداخت : {data?.payment?.paymentMethod?.title || "—"}
                </div>
              </td>
              <td className="sumLabel" colSpan={2}>
                مبلغ سفارش
              </td>
              <td>{price(total)}</td>
            </tr>
            <tr>
              <td className="sumLabel" colSpan={2}>
                هزینه ارسال
              </td>
              <td>{price(deliveryPrice)}</td>
            </tr>
            <tr>
              <td className="sumLabel" colSpan={2}>
                تخفیف
              </td>
              <td>{price(discount)}</td>
            </tr>
            <tr>
              <td className="sumLabel" colSpan={2}>
                مبلغ قابل پرداخت
              </td>
              <td>
                <b>{price(payable)}</b>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Invoice;
